<template>
  <div class="booking-client">
    <Headline :value="data.headline" :number="data.sectionNumber" />

    <div class="booking-client__form">
      <div class="booking-client__form--row">
        <TextInput name="firstName" placeholder="Wpisz imię (imiona) z dokumentu osoby rezerwującej"
          label="Imię (imiona)*" :firstLetterUppercase="true"
          @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'firstName')"
          data-vv-validate-on="change" data-vv-as="imię"
          v-model="user.firstName" validateAs="firstName"
          v-validate="{ required: true, regex: nameRegex }" />
        <TextInput name="lastName" placeholder="Wpisz nazwisko z dokumentu osoby rezerwującej"
          label="Nazwisko*" :firstLetterUppercase="true"
          @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'lastName')"
          data-vv-validate-on="change" data-vv-as="nazwisko"
          v-model="user.lastName" validateAs="lastName"
          v-validate="{ required: true, regex: nameRegex }" />
      </div>

      <div class="booking-client__form--row booking-client__form--phone-span">
        <TextInput name="email" placeholder="Wpisz adres email osoby rezerwującej" label="Email*"
          @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'email')"
          data-vv-validate-on="change"
          v-model="user.email" validateAs="email"
          v-validate="{ required: true, regex: emailRegex, email: true }" />
        <PhoneNumber 
          :options="phonePrefixList" 
          name="phoneNumber"
          :value="user.phone"
          @input="updateField($event, 'phone')" 
        />
      </div>

      <div class="booking-client__form--row">
        <TextInput name="street" placeholder="Wpisz nazwę ulicy / osiedla /alei itp." label="Ulica*"
          @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'street')"
          data-vv-validate-on="change" :firstLetterUppercase="true"
          v-model="user.street" validateAs="street"
          v-validate="{ required: true, regex: streetRegex }" />
        <div class="booking-client__form--grid-column street-number">
          <TextInput name="houseNumber" label="Nr domu*"
            @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'houseNumber')"
            data-vv-validate-on="change"
            v-model="user.houseNumber" validateAs="houseNumber"
            v-validate="{ required: true, regex: /^[0-9]{1,}[A-Za-z0-9\/\-]{0,}$/ }" />
          <TextInput name="apartmentNumber" label="Nr mieszkania"
            @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'apartmentNumber')"
            data-vv-validate-on="change"
            v-model="user.apartmentNumber" validateAs="apartmentNumber"
            v-validate="{ required: false, regex: /^[0-9]{1,}$/ }" />
        </div>
      </div>

      <div class="booking-client__form--row booking-client__form--city">
        <TextInput name="city" placeholder="Miejscowość zamieszkania" label="Miejscowość*"
          @focus="onFocusTextInput" @blur="onBlurTextInput" @change="updateField($event.target.value, 'city')"
          data-vv-validate-on="change" :firstLetterUppercase="true"
          v-model="user.city" validateAs="city"
          v-validate="{ required: true, regex: cityRegex }" />
        <div class="booking-client__form--grid-column booking-client__form--zip-code">
          <!-- No matter if you type here first od second part, it will be validated correctly -->
          <ZipCode 
            label="Kod pocztowy*" 
            name="zipCodeSecondPart"
            :value="user.zipCode"
            @input="updateField($event, 'zipCode')" 
          />
        </div>
      </div>

      <div class="booking-client__form--checklist">
        <CheckBox v-model="additionalFields" @input="onAdditionalFieldsChange" />
        <CheckBoxLabel @input="toggleAdditionalFields" :enabled="true" :text="data.checkboxLabel" :html="true" tabindex="0" />
      </div>

      <div v-if="additionalFields" class="booking-client__form--row booking-client__form--gender-span">
        <div class="booking-client__form--grid-column gender-list">
          <RadioListInputBox label="Płeć*" tabindex="0" :class="{
            'selection-box--blue-border': isMobile,
            'input-box--blue-border': !isMobile }"
            :highlight="dropdowns.gender" vvName="gender" vvKey="gender"
            @click="toggle('gender')" @close="toggle('gender')" @input="updateField($event, 'gender')"
            v-model="user.gender" validateAs="gender" rules="required"
            :options="genderList" />
        </div>
        <div class="booking-client__form--grid-column datepicker">
          <DateInput placeholder="DD-MM-RRRR" tabindex="0" class="date-input--blue-border"
            name="birthday" title="Data urodzenia" key="birthday"
            label="Data urodzenia*" :min="minDate" :max="maxDate" useNativeDateInput @input="updateField($event, 'birthday')"
            v-model="user.birthday" validateAs="birthday" v-validate="'moment_not_zero'">
            <Icon slot="icon" icon="calendar" class="calendar-booking-client" />
          </DateInput>
        </div>
      </div>
    </div>

    <template v-if="offer.AdultsCount">
      <Headline value="Dane <b>uczestników wycieczki</b>" :number="data.sectionNumber + 1" />
      <Participants 
        :data="offer"
        :user="user"
        :genderList="genderList"
        :backOfferForm="backOfferForm"
        :additionalFields="additionalFields"
        @update-user-field="updateUserFromParticipant"
      />
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapState, mapActions } from 'vuex'

import Participants from '@/components/booking/Participants'
import CheckBox from '@/components/CheckBox.vue'
import CheckBoxLabel from '@/components/CheckBoxLabel.vue'
import DateInput from '@/components/DateInput'
import PhoneNumber from '@/components/forms/fields/PhoneNumber.vue'
import TextInput from '@/components/forms/fields/TextInput.vue'
import ZipCode from '@/components/forms/fields/ZipCode'
import Headline from '@/components/forms/Headline'
import Icon from '@/components/Icon.vue'
import RadioListInputBox from '@/components/RadioListInputBox'
import DropdownHandler from '@/components/search/DropdownHandler'
import SelectBox from '@/components/search/SelectBox'
import Store from '@/store/index'
import { SET_USER_FIELD, SET_FIRST_PARTICIPANT_FIELD, FILL_FIRST_PARTICIPANT_FIELDS } from '@/store/mutation-types'
import MediaDetector from '@/utils/MediaDetector'
import FormStorageMixin from '@/mixins/FormStorageMixin'

@Component({
  store: Store,
  mixins: [
    MediaDetector,
    DropdownHandler,
    FormStorageMixin,
  ],
  components: {
    Headline,
    TextInput,
    PhoneNumber,
    CheckBox,
    CheckBoxLabel,
    SelectBox,
    RadioListInputBox,
    DateInput,
    ZipCode,
    Icon,
    Participants,
  },
  props: {
    data: {
      type: Object,
      required: false,
      default: () => {},
    },
    backOfferForm: {
      type: HTMLFormElement,
      required: false,
    },
  },
  computed: {
    ...mapState({
      user: state => state.Booking.user,
      offer: state => state.Booking.offer,
    }),
  },
  methods: {
    ...mapActions({
      setUserField: `Booking/${SET_USER_FIELD}`,
      setFirstParticipantField: `Booking/${SET_FIRST_PARTICIPANT_FIELD}`,
      fillFirstParticipantFields: `Booking/${FILL_FIRST_PARTICIPANT_FIELDS}`,
    }),
    updateUserFromParticipant ({ field, value }) {
      // Update user data in Vuex store
      this.setUserField({ field, value })
      
      // Update local user data and validate
      if (field === 'birthday') {
        this.$set(this.user, field, moment.isMoment(value) ? value : moment(value))
      } else {
        this.$set(this.user, field, value)
      }

      // Force update specific components if needed
      this.$nextTick(() => {
        if (field === 'birthday') {
          const dateInput = this.$children.find(child => 
            child.$options.name === 'DateInput' && child.$props.name === 'birthday'
          )
          if (dateInput) {
            dateInput.$emit('input', value)
          }
        }
      })
    },
  },
  mounted () {
    this.loadStoredData()
    // Get initial state from storage
    const storedData = this.loadFromStorage() || {}
    if (storedData.clientForm) {
      this.additionalFields = storedData.clientForm.additionalFields || false
    }

    // Listen for updates from Participants component
    this.$root.$on('update-client-form', ({ field, value }) => {
      this.updateField(value, field)
    })
  },
  watch: {
    additionalFields: {
      immediate: true,
      handler (newValue) {
        if (newValue) {
          this.fillFirstParticipantWithUserData()
        }
      },
    },
    'user.birthday': {
      immediate: true,
      handler (newVal) {
        if (newVal && this.additionalFields) {
          const currentData = this.loadFromStorage() || {}
          this.saveToStorage({
            ...currentData,
            clientForm: {
              ...currentData.clientForm,
              birthday: moment(newVal).format(),
            },
          })
        }
      },
    },
  },
})
export default class BookingClientForm extends Vue {
  additionalFields = false
  minDate = moment().subtract(100, 'years').startOf('day') 
  maxDate = moment().subtract(18, 'years').startOf('day')
  streetRegex;
  cityRegex;
  dropdowns = {
    gender: false,
  }

  created () {
    const letters = 'A-Za-zżźćńółęąśŻŹĆĄŚĘŁÓŃ\\s'
    const numbers = '0-9'
    const specials = '\\-\\, '
    const dot = '\\.'
    this.nameRegex = this.validationRules([ letters, '\\-', dot ], [ 3, 4, 2, 2 ], [ '\\-', dot ], [ '\\-' ], 2, [])
    this.streetRegex = this.validationRules([ letters, numbers, specials, dot ], [ 3, 4, 2, 2 ], [ specials, dot ], [ specials ], 2, [ '(?!\\d*$)' ])
    this.cityRegex = this.validationRules([ letters, '\\- ' ], [ 3, 2 ], [ '\\- ' ], [ '\\- ' ], 2, [])
    this.emailRegex = /^(?!.*(([.\-_]{2})|[.\-_]@)|(@[.\-_]))[a-zA-Z0-9][a-zA-Z0-9.\-_]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/
  }

  loadStoredData () {
    const storedData = this.loadFromStorage()

    if (storedData && storedData.clientForm) {
      this.additionalFields = storedData.clientForm.additionalFields || false

      // Load all stored fields into user data
      Object.keys(storedData.clientForm).forEach(field => {
        if (field !== 'additionalFields' && storedData.clientForm[field]) {
          let value = storedData.clientForm[field]
          
          if (field === 'birthday' && value) {
            value = moment(value)
          }

          this.setUserField({ field, value })
        }
      })

      // Handle special components after Vue has updated
      this.$nextTick(() => {
        // Handle phone number
        if (storedData.clientForm.phone) {
          const phoneComponent = this.$children.find(child => child.$options.name === 'PhoneNumber')
          if (phoneComponent) {
            phoneComponent.$emit('input', storedData.clientForm.phone)
          }
        }

        // Handle zip code
        if (storedData.clientForm.zipCode) {
          const zipComponent = this.$children.find(child => child.$options.name === 'ZipCode')
          if (zipComponent) {
            zipComponent.$emit('input', storedData.clientForm.zipCode)
          }
        }

        // Handle birthday
        if (storedData.clientForm.birthday) {
          const dateInput = this.$children.find(child => child.$options.name === 'DateInput')
          if (dateInput) {
            dateInput.$emit('input', moment(storedData.clientForm.birthday))
          }
        }
      })
    }
  }

  updateField (value, field) {
    // Update local user data
    this.$set(this.user, field, value)
    
    // Get current storage data
    const currentData = this.loadFromStorage() || {}
    
    // Format birthday if needed
    const formattedValue = field === 'birthday' && value ? 
      (moment.isMoment(value) ? value.format() : value) : 
      value

    // Only validate if field is empty or has been modified
    if (!value || this.user[field] !== value) {
      this.$validator.validate(field)
    }

    // Update storage with new field value
    this.saveToStorage({
      ...currentData,
      clientForm: {
        ...currentData.clientForm,
        [field]: formattedValue,
      },
    })

    // If additionalFields is true, also update first participant
    if (this.additionalFields) {
      const currentParticipants = currentData.participants || []
      this.saveToStorage({
        ...currentData,
        clientForm: {
          ...currentData.clientForm,
          additionalFields: this.additionalFields,
          [field]: formattedValue,
        },
        participants: [
          {
            ...(currentParticipants[0] || {}),
            id: 1,
            type: 'adult',
            [field]: formattedValue,
            title: 'Osoba dorosła',
          },
          ...currentParticipants.slice(1),
        ],
      })
    }
  }

  validationRules (groups, maxRepeatGroups, cantStartWith, cantEndWith, minCharacters, custom) {

    const group = groups.join('')
    const repeats = []
    groups.forEach((grp, index) => {
      const maxRepeats = maxRepeatGroups[index]
      if (maxRepeats < 1) {
        throw new Error('max repeats value must be a value of max repeats, values smaller than 2 are forbidden')
      }
      repeats.push(`(?!.*(?<repeat${index}>[${grp}])(\\k<repeat${index}>){${maxRepeats - 1}})`)
    })
    const cantStartWithMatch = cantStartWith.length > 0 ? `(?![${cantStartWith.join('')}])` : ''
    const cantEndWithMatch = cantEndWith.length > 0 ? `(?!.*[${cantEndWith.join('')}]$)` : ''
    const preventGroupFromRepeating = repeats.join('')
    const customJoined = custom.join('')
    const joined = `^${customJoined}${preventGroupFromRepeating}${cantStartWithMatch}${cantEndWithMatch}[${group}]{${minCharacters},}$`

    return new RegExp(joined)
  }

  get phonePrefixList () {
    return [ '+48' ]
  }

  get genderList () {
    return [ '', 'kobieta', 'mężczyzna' ]
  }

  get formattedValidationMaxDate () {
    return moment(this.maxDate).add(1, 'day').format('DD.MM.YYYY')
  }

  get formattedValidationMinDate () {
    return moment(this.minDate).subtract(1, 'day').format('DD.MM.YYYY')
  }

  get userDataForFirstParticipant () {
    return {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      gender: this.user.gender,
      birthday: this.user.birthday,
    }
  }

  fillFirstParticipantWithUserData () {
    if (this.additionalFields) {
      const currentData = this.loadFromStorage() || {}
      const currentParticipants = currentData.participants || []
      
      // Format birthday properly if it exists
      const formattedBirthday = this.user.birthday ? 
        (moment.isMoment(this.user.birthday) ? this.user.birthday.format('YYYY-MM-DD') : moment(this.user.birthday).format('YYYY-MM-DD')) : 
        null

      const firstParticipant = {
        ...(currentParticipants[0] || {}),
        id: 1,
        type: 'adult',
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        gender: this.user.gender,
        birthday: formattedBirthday,
        title: 'Osoba dorosła',
      }

      // Save to storage with proper data structure
      this.saveToStorage({
        ...currentData,
        clientForm: {
          ...currentData.clientForm,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          gender: this.user.gender,
          birthday: formattedBirthday,
          additionalFields: true,
        },
        participants: [
          firstParticipant,
          ...currentParticipants.slice(1),
        ],
      })

      // Force update after storage change
      this.$nextTick(() => {
        // Update Vuex store
        this.fillFirstParticipantFields(this.userDataForFirstParticipant)
        // Emit event to notify Participants component
        this.$emit('participant-data-updated', firstParticipant)
      })
    }
  }

  onAdditionalFieldsChange (value) {
    this.additionalFields = value
    
    // Get current storage data
    const currentData = this.loadFromStorage() || {}
    const currentClientForm = currentData.clientForm || {}

    const storageUpdate = {
      ...currentData,
      clientForm: {
        ...currentClientForm,
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        gender: this.user.gender || '',
        birthday: this.user.birthday ? moment(this.user.birthday).format('YYYY-MM-DD') : '',
        additionalFields: this.additionalFields,
      },
    }

    // Clear participants if turning off
    if (!value) {
      storageUpdate.participants = []
    } else {
      // Add first participant if turning on
      const firstParticipant = {
        id: 1,
        type: 'adult',
        firstName: this.user.firstName || '',
        lastName: this.user.lastName || '',
        gender: this.user.gender || '',
        birthday: this.user.birthday ? moment(this.user.birthday).format('YYYY-MM-DD') : '',
        title: 'Osoba dorosła',
      }
      storageUpdate.participants = [ firstParticipant ]
      this.fillFirstParticipantWithUserData()
    }

    // Save to storage and force update
    this.saveToStorage(storageUpdate)
    this.$root.$emit('storage-updated', storageUpdate)
  }

  setZipCode (code) {
    this.zipCode = code
  }

  onFocusTextInput (e) {
    return e.target.closest('.text-input').classList.add('text-input--focus')
  }

  onBlurTextInput (e) {
    return e.target.closest('.text-input').classList.remove('text-input--focus')
  }
}
</script>

<style lang="scss" scoped>
.booking-client {
  background-color: #eeeeee;
  z-index: 2;
  display: grid;
  grid-row-gap: 10px;

  @include mediaFluid(desktop) {
    grid-row-gap: 20px;
  }

  &__form {
    padding: 25px 20px;
    background-color: #fff;
    display: grid;
    grid-row-gap: 18px;

    @include mediaFluid(tablet) {
      grid-row-gap: 20px;
    }

    &--grid-column {
      display: grid;
      grid-column-gap: 20px;
    }
  }

  &__form--row {
    display: grid;
    grid-template-columns: 1fr;
    grid-column-gap: 20px;
    grid-row-gap: 17px;

    @include mediaFluid(tablet) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__form--phone-span {
    .phone-number {
      grid-row-end: span 1;
    }

    .text-input {
      grid-row-end: span 2;
    }
  }

  &__form--gender-span {
    .gender-list {
      grid-row-end: span 1;
    }

    .datepicker {
      grid-row-end: span 2;
    }
  }

  &__form--city {
    grid-template-columns: 1fr 85px;

    @include mediaFluid(tablet) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__form--checklist {
    display: flex;
    margin-top: 10px;
    align-items: flex-start;

    @include mediaFluid(tablet) {
      margin-top: 0;
    }

    .checkbox-label {
      font-weight: normal;
      font-size: 12px;
      font-family: 'Gotham Book';
      color: #005CA9;
      font-size: 12px;
      line-height: 1.2;
      margin-left: 10px;
      margin-top: 1px;
    }

    .checkbox {
      flex: 1 0 auto;
      margin-top: 1px;
      max-width: 14px;
      background: transparent;
      border: 2px solid #005CA9;

      &::before {
        top: -6px;
        left: 2px;
      }
    }
  }

  &__form--zip-code {
    @include mediaFluid(tablet) {
      grid-template-columns: 85px 1fr;
    }
  }

  .phone-number {
    color: #005CA9;
    align-items: inherit;
  }

  .street-number {
    grid-template-columns: 85px 85px 1fr;
  }

  .gender-list {
    @include mediaFluid(tablet) {
      grid-template-columns: 1fr 1fr;
    }
  }

  .datepicker {
    color: #005CA9;
  }
}
</style>

<style lang="scss">
.booking-client {
  .text-input, .input-box, .zip-code {
    color: #005CA9;
    font-family: 'Gotham Book';
    line-height: 1.45;

    &__label {
      font-size: 11px;
    }

    &__value {
      font-size: 12px;
    }

    &__icon {
      color: #005CA9;
    }
  }

  .text-input {
    background-image: none;

    &__value input {
      @include mediaFluid(tablet) {
        padding: 6px 0 8px;
      }
    }
  }

  .checkbox:not(.checkbox--checked) {
    color: transparent;
  }

  .input-box__value {
    padding: 3px 0 7px;
  }

  .input-box__icon {
    padding-top: 9px;
    padding-right: 10px;
  }

  .headline {
    margin: 0;
    padding: 10px 20px;

    @include mediaFluid(tablet) {
      padding-left: 0;
      padding-right: 0;
      margin: 5px 0 0;
    }

    &__number {
      margin-left: 4px;
    }

    span {
      line-height: 1.2;
      font-size: 1.3em;

      @include mediaFluid(tablet) {
        line-height: inherit;
        font-size: 1.6em;
      }
    }
  }
  
  .selection-box {
    line-height: 1.35;

    &__label {
      font-size: 12px;
      font-family: 'Gotham Book';
    }
  }

  select {
    color: inherit;
    text-transform: none;
    font-size: 12px;

    @supports (-webkit-overflow-scrolling: touch) {
      padding-top: 7px;
      padding-bottom: 8px;
    }
  }

  .date-input {
    font-family: 'Gotham Book';
    color: #005CA9;

    @include media(tablet) {
      width: 210px;
      line-height: 1.4;
    }

    &__value {
      display: block;
      width: calc(100% - 28px);
      padding: 2px 0 6px;
      opacity: 1;
      font-size: 13px;
    }

    &__label {
      font-size: 11px;
      margin-bottom: 1px;
    }

    &__placeholder {
      font-size: 13px;
    }

    &__dropdown {
      margin-top: 9px;
      width: 240px;

      .figure::before {
        font-size: 8px;
        font-weight: bold !important;
        margin-left: 3px;
      }
    }
  }

  .gender-list {
    color: #005CA9;

    .input-box__value {
      line-height: 1.4;
    }

    .input-box__icon {
      @include mediaFluid(desktop) {
        height: 27px;
      }
    }

    .field-valid::before {
      @include mediaFluid(tablet) {
        top: 23px;
      }
    }
  }

  .radiolist-item input {
    display: none;
  }

  .radiolist-item__label {
    padding: 0;

    &--selected {
      color: #009FEA;
    }

    &::before,
    &::after, {
      display: none;
    }
  }

  .zip-code__value {
    padding-top: 1px;
  }

  .calendar-booking-client {
    float: right;
    position: relative;
    font-size: 18px;
    color: #005CA9;
    width: 28px;
    height: 28px;
    text-align: center;
    margin-top: -2px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/booking/ClientForm.vue